import { isProblemDetails, type ProblemDetails } from "./problem-details";

/**
 * The inline failure line under a form (`002` T011, constitution VI).
 *
 * `ErrorSurface` already reports a failed *read* — a bordered alert that takes the whole column.
 * That is the wrong shape above a four-field sign-in card, which is why this exists; it is **not**
 * a second parser. Both narrow through the same `isProblemDetails`, so a service that changes its
 * problem document changes one file.
 *
 * There is deliberately no generic-message prop, for the same reason `ErrorSurface` has none: a
 * form that cannot say what the server rejected has not handled the rejection, and
 * "Something went wrong" is the defect this component exists to prevent.
 *
 * ## `title` is not always the whole story, and `detail` is not always present
 *
 * RFC 9457 lets a service send either. Both are rendered when both arrive, and a document with
 * neither still renders its `status` rather than collapsing to nothing — an empty error slot reads
 * as success.
 */
export function FieldError({
  error,
  /**
   * Overrides the document's own words. The one sanctioned use is a refusal the API deliberately
   * words identically for several causes — `002` SC-007's six sign-in refusals — where echoing the
   * body would invite a future contributor to word them apart. Everything else passes `error`.
   */
  message,
  id,
}: {
  error?: unknown;
  message?: string;
  id?: string;
}) {
  const problem: ProblemDetails | null = isProblemDetails(error) ? error : null;

  if (!message && !problem && error === undefined) return null;

  return (
    <p
      id={id}
      role="alert"
      data-slot="field-error"
      className="mt-0.5 flex flex-col gap-0.5 text-[13px] text-red"
    >
      {message ? <span className="font-medium">{message}</span> : null}

      {message ? null : problem ? (
        <>
          {problem.title ? <span className="font-medium">{problem.title}</span> : null}
          {problem.detail ? <span className="font-normal">{problem.detail}</span> : null}
          {!problem.title && !problem.detail && problem.status ? (
            <span className="font-medium">The request was rejected ({problem.status}).</span>
          ) : null}
        </>
      ) : (
        <span className="font-medium">
          {error instanceof Error ? error.message : String(error)}
        </span>
      )}
    </p>
  );
}
